import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import {
  Users, Shield, ShieldOff, Trash2, Search, Filter,
  CheckCircle2, AlertTriangle, Mail, X, UserCog,
  Activity, TrendingUp, UserCheck,
} from "lucide-react";

const AVAILABLE_ROLES = [
  "user", "founder", "admin", "support", "partner_org", "enterprise",
  "mentor", "conseiller", "partner_institutional", "guest",
];

const ROLE_LABELS = {
  user: "Utilisateur", founder: "Fondateur", admin: "Administrateur",
  support: "Support", partner_org: "Partenaire", enterprise: "Enterprise",
  mentor: "Mentor", conseiller: "Conseiller", partner_institutional: "Partenaire institutionnel",
  guest: "Invité",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [suspendedFilter, setSuspendedFilter] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [suspendingUser, setSuspendingUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      if (suspendedFilter !== "") params.set("suspended", suspendedFilter);
      params.set("limit", "50");
      const [u, s] = await Promise.all([
        api.get(`/admin/users?${params}`),
        api.get(`/admin/audit/stats`),
      ]);
      setUsers(u.data.items || []);
      setTotal(u.data.total || 0);
      setStats(s.data);
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter, suspendedFilter]);

  // Debounce search input — auto-fires load 400ms after typing stops
  useEffect(() => {
    const handle = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [q]);

  const saveRoles = async (user, roles) => {
    try {
      await api.put(`/admin/users/${user.user_id}/roles`, { user_id: user.user_id, roles });
      altioraToast.success("Rôles mis à jour");
      setEditingUser(null);
      load();
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Échec");
    }
  };

  const suspend = async () => {
    if (!suspendReason || suspendReason.length < 3) { altioraToast.error("Raison requise (3 caractères min)"); return; }
    try {
      await api.post(`/admin/users/${suspendingUser.user_id}/suspend`, { user_id: suspendingUser.user_id, reason: suspendReason });
      altioraToast.success("Utilisateur suspendu");
      setSuspendingUser(null);
      setSuspendReason("");
      load();
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Échec");
    }
  };

  const reactivate = async (user) => {
    try {
      await api.post(`/admin/users/${user.user_id}/reactivate`);
      altioraToast.success("Utilisateur réactivé");
      load();
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Échec");
    }
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Supprimer définitivement ${user.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      altioraToast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Échec");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="admin-users-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1">Console Fondateur</div>
          <h1 className="text-3xl font-display text-[#0B2545] tracking-tight">Gestion des utilisateurs</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <StatBox label="Total" value={stats.users_total} icon={Users} testId="admin-stat-total" />
            <StatBox label="Fondateurs" value={stats.users_founders} icon={Shield} tone="gold" testId="admin-stat-founders" />
            <StatBox label="Vérifiés" value={stats.users_verified} icon={UserCheck} testId="admin-stat-verified" />
            <StatBox label="Suspendus" value={stats.users_suspended} icon={ShieldOff} tone="warn" testId="admin-stat-suspended" />
            <StatBox label="Connexions 24h" value={stats.logins_24h} icon={Activity} testId="admin-stat-logins" />
            <StatBox label="Échecs 24h" value={stats.failed_24h} icon={AlertTriangle} tone={stats.failed_24h > 5 ? "warn" : "neutral"} testId="admin-stat-failed" />
            <StatBox label="Suspects 7j" value={stats.suspicious_7d} icon={TrendingUp} tone={stats.suspicious_7d > 0 ? "warn" : "neutral"} testId="admin-stat-suspicious" />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="admin-filters">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B5B62]" strokeWidth={1.5} />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Rechercher email, nom, username…"
              className="w-full border border-[#E4E1D8] rounded-md pl-8 pr-3 py-2 text-sm"
            />
          </div>
          <select
            data-testid="admin-role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-[#E4E1D8] rounded-md px-3 py-2 text-sm"
          >
            <option value="">Tous rôles</option>
            {AVAILABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <select
            data-testid="admin-suspended-filter"
            value={suspendedFilter}
            onChange={(e) => setSuspendedFilter(e.target.value)}
            className="border border-[#E4E1D8] rounded-md px-3 py-2 text-sm"
          >
            <option value="">Actifs + suspendus</option>
            <option value="false">Actifs seulement</option>
            <option value="true">Suspendus seulement</option>
          </select>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 border border-[#E4E1D8] px-3 py-2 text-sm text-[#1F2937] hover:bg-white rounded-md"
          >
            <Filter className="h-3.5 w-3.5" strokeWidth={1.5} /> Filtrer
          </button>
        </div>

        {/* Table */}
        <div className="border border-[#E4E1D8] bg-white rounded-md overflow-hidden" data-testid="admin-users-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[#5B5B62] border-b border-[#E4E1D8] bg-[#F8F7F3]">
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Nom</div>
            <div className="col-span-3">Rôles</div>
            <div className="col-span-2">Dernière connexion</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="px-4 py-8 text-sm text-[#5B5B62]">Chargement…</div>
          ) : users.length === 0 ? (
            <div className="px-4 py-8 text-sm text-[#5B5B62] text-center">Aucun utilisateur.</div>
          ) : (
            users.map((u) => (
              <div key={u.user_id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#F1EFE9] hover:bg-[#F8F7F3] text-sm items-center" data-testid={`admin-user-${u.user_id}`}>
                <div className="col-span-3 truncate">
                  <div className="text-[#1F2937] flex items-center gap-1">
                    {u.email}
                    {u.email_verified && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                  </div>
                  {u.suspended && <div className="text-[10px] text-red-600 font-mono">SUSPENDU</div>}
                </div>
                <div className="col-span-2 truncate text-[#1F2937]">{u.name}</div>
                <div className="col-span-3 flex flex-wrap gap-1">
                  {(u.roles || []).map((r) => (
                    <span key={r} className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${
                      r === "founder" ? "bg-[#FBF6EB] text-[#7A5B14] border-[#E7D9B3]" :
                      r === "admin" ? "bg-[#F1EFE9] text-[#0B2545] border-[#E4E1D8]" :
                      "bg-white text-[#5B5B62] border-[#E4E1D8]"
                    }`}>{r}</span>
                  ))}
                </div>
                <div className="col-span-2 text-[11px] font-mono text-[#8B8B8F]">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("fr-CA") : "jamais"}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button data-testid={`admin-edit-roles-${u.user_id}`} onClick={() => setEditingUser(u)} title="Modifier rôles" className="p-1.5 text-[#5B5B62] hover:text-[#0B2545] rounded"><UserCog className="h-4 w-4" strokeWidth={1.5} /></button>
                  {u.suspended ? (
                    <button data-testid={`admin-reactivate-${u.user_id}`} onClick={() => reactivate(u)} title="Réactiver" className="p-1.5 text-emerald-600 hover:text-emerald-700 rounded"><CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /></button>
                  ) : (
                    <button data-testid={`admin-suspend-${u.user_id}`} onClick={() => setSuspendingUser(u)} title="Suspendre" className="p-1.5 text-amber-600 hover:text-amber-700 rounded"><ShieldOff className="h-4 w-4" strokeWidth={1.5} /></button>
                  )}
                  <button data-testid={`admin-delete-${u.user_id}`} onClick={() => removeUser(u)} title="Supprimer" className="p-1.5 text-red-600 hover:text-red-700 rounded"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mt-2 text-[11px] font-mono text-[#8B8B8F]">
          {users.length} affichés · {total} au total
        </div>
      </div>

      {/* Edit roles modal */}
      {editingUser && <EditRolesModal user={editingUser} onClose={() => setEditingUser(null)} onSave={(roles) => saveRoles(editingUser, roles)} />}

      {/* Suspend modal */}
      {suspendingUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSuspendingUser(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-suspend-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display text-[#0B2545]">Suspendre {suspendingUser.email}</h3>
              <button onClick={() => setSuspendingUser(null)}><X className="h-5 w-5 text-[#5B5B62]" /></button>
            </div>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">Motif</div>
              <textarea
                data-testid="admin-suspend-reason"
                value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)}
                className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm"
                rows="3" placeholder="Motif de la suspension (≥ 3 caractères)"
              />
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setSuspendingUser(null)} className="border border-[#E4E1D8] px-4 py-2 text-sm rounded-md">Annuler</button>
              <button data-testid="admin-suspend-confirm" onClick={suspend} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm rounded-md">Suspendre</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditRolesModal({ user, onClose, onSave }) {
  const [roles, setRoles] = useState(user.roles || ["user"]);
  const toggle = (r) => setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-roles-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display text-[#0B2545]">Modifier les rôles</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#5B5B62]" /></button>
        </div>
        <div className="text-sm text-[#5B5B62] mb-3"><Mail className="h-3 w-3 inline mr-1" />{user.email}</div>
        <div className="space-y-1 mb-4 max-h-72 overflow-y-auto">
          {AVAILABLE_ROLES.map((r) => (
            <label key={r} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F8F7F3] rounded cursor-pointer">
              <input
                type="checkbox"
                data-testid={`admin-role-${r}`}
                checked={roles.includes(r)}
                onChange={() => toggle(r)}
                className="rounded border-[#E4E1D8] text-[#0B2545] focus:ring-[#0B2545]"
              />
              <span className="text-sm">{ROLE_LABELS[r]} <span className="text-[10px] font-mono text-[#8B8B8F]">({r})</span></span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border border-[#E4E1D8] px-4 py-2 text-sm rounded-md">Annuler</button>
          <button data-testid="admin-roles-save" onClick={() => onSave(roles)} className="bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm rounded-md">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, tone = "neutral", testId }) {
  const toneCls = tone === "gold" ? "border-[#E7D9B3] bg-[#FBF6EB]" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-[#E4E1D8] bg-white";
  return (
    <div className={`border ${toneCls} rounded-md p-3`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5B5B62]">{label}</div>
        <Icon className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={1.5} />
      </div>
      <div className="mt-1 text-xl font-display text-[#0B2545]">{value}</div>
    </div>
  );
}
